import { type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/cn';

type Tone = 'info' | 'success' | 'warning' | 'danger';

interface AlertProps {
  tone?: Tone;
  title?: string;
  children: ReactNode;
  className?: string;
}

const toneClasses: Record<Tone, string> = {
  info: 'border-brand-blue/30 bg-brand-blue/5 text-brand-blue',
  success: 'border-brand-success/30 bg-brand-success/5 text-brand-success',
  warning: 'border-brand-warning/30 bg-brand-warning/5 text-brand-warning',
  danger: 'border-brand-danger/30 bg-brand-danger/5 text-brand-danger',
};

const toneIcons: Record<Tone, typeof AlertCircle> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
};

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  const Icon = toneIcons[tone];
  return (
    <div
      role="alert"
      className={cn(
        'flex gap-3 rounded-md border px-4 py-3 text-sm',
        toneClasses[tone],
        className,
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1">
        {title ? <p className="font-medium">{title}</p> : null}
        <div className={cn(title && 'mt-1 text-brand-charcoal/80')}>{children}</div>
      </div>
    </div>
  );
}
